export type Option = { value: string; label: string }
export type KeyedOption = { key: string; label: string; hint?: string }

export const HPI = [['hpi', 'HPI']] as const

export type ReasonForDischarge = 'programCompletion' | 'earlyDisenrollment' | 'voluntaryDisenrollment'

export const REASON_OPTIONS: { value: ReasonForDischarge; label: string }[] = [
  { value: 'programCompletion', label: 'Program Completion' },
  { value: 'earlyDisenrollment', label: 'Early Disenrollment Due to Lack of Program Engagement' },
  { value: 'voluntaryDisenrollment', label: "Voluntary Program Disenrollment at Pt's Request" },
]

export const REASON_FOR_CALL_OPTIONS = [
  { value: 'PROGRAM_ENGAGEMENT', label: 'Program engagement' },
  { value: 'NEW_STRESSOR_OR_LIFE_EVENT', label: 'New stressor or life event' },
  { value: 'SYMPTOM_MANAGEMENT_AND_OR_ESCALATION', label: 'Symptom management and/or escalation' },
  { value: 'CRISIS_MANAGEMENT_AND_OR_SAFETY_PLANNING', label: 'Crisis management and/or safety planning' },
  { value: 'SUPPORT_SYSTEM_ISSUE', label: 'Support system issues' },
  { value: 'THERAPEUTIC_ALLIANCE_OR_RELATIONSHIP', label: 'Therapeutic alliance/relationship' },
  { value: 'END_OF_PROGRAM_WRAP_UP', label: 'End of program wrap-up' },
] as const

export const EMPLOYMENT_OPTIONS = [
  { value: 'WORKING', label: 'Working' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'DISABLED', label: 'Disabled' },
  { value: 'RETIRED', label: 'Retired' },
  { value: 'UNEMPLOYED', label: 'Unemployed' },
  { value: 'STAY_AT_HOME_PARENT', label: 'Stay-at-home parent' },
] as const

export const PHYSICAL_APPEARANCE_OPTIONS = [
  { value: 'normal', label: 'All findings are within normal limits' },
  { value: 'abnormal', label: 'One or more findings are outside normal limits' },
]

export const PSYCHIATRIC_HISTORY = [
  ['traumaHistory', 'Trauma History'],
  ['postTraumaticStressSymptoms', 'Post-Traumatic Stress Symptoms'],
  ['anxietySymptoms', 'Anxiety Symptoms'],
  ['moodSymptoms', 'Mood Symptoms'],
  ['depressionAndManicSymptoms', 'Depression and Manic Symptoms'],
  ['lethalityRisk', 'Lethality Risk (Self-Harm or Harm to Others)'],
  ['psychoticSymptoms', 'Psychotic Symptoms (Hallucinations, Delusions, Disorganization)'],
  ['sleepPatterns', 'Sleep Patterns and Concerns'],
  ['appetiteAndNutrition', 'Appetite and Nutritional Intake'],
  ['sensoryPerceptions', 'Sensory Perceptions or Disturbances'],
  ['attentionDeficitSymptoms', 'Attention-Deficit and Hyperactivity Symptoms'],
] as const

export const SOCIAL_AND_FUNCTIONAL_HISTORY = [
  ['educationEmploymentStatus', 'Education Employment Status'],
  ['substanceUseHistory', 'Substance Use History (Alcohol, Tobacco, Drugs)'],
  ['medicalHistory', 'Medical History (Chronic Illnesses, Surgeries, Relevant Conditions)'],
  ['familyPsychiatricHistory', 'Family Psychiatric and Medical History'],
] as const

export const SUMMARY = [
  ['therapeuticInterventions', 'Therapeutic Interventions'],
  ['lifestyleRecommendations', 'Lifestyle and Behavioral Recommendations'],
  ['followUpPlan', 'Follow-up Plan and Labwork'],
  ['mentalStatusExamination', 'Mental Status Examination'],
  ['clinicalImpression', 'Clinical Impression'],
] as const

export const BURNOUT_OPTIONS: { value: '1' | '2' | '3' | '4' | '5'; label: string }[] = [
  { value: '1', label: 'I enjoy my work. I have no symptoms of burnout.' },
  {
    value: '2',
    label:
      'Occasionally I am under stress, and I don’t always have as much energy as I once did, but I don’t feel burned out.',
  },
  {
    value: '3',
    label:
      'I am definitely burning out and have one or more symptoms of burnout, such as physical or emotional exhaustion.',
  },
  {
    value: '4',
    label: 'The symptoms of burnout that I’m experiencing won’t go away. I think about frustration at work a lot.',
  },
  {
    value: '5',
    label:
      'I feel completely burned out and often wonder if I can go on. I am at the point where I may need some changes or may need to seek some sort of help.',
  },
]

export const HEALTH_HISTORY_OPTIONS: KeyedOption[] = [
  { key: 'THERAPY', label: 'Therapy' },
  { key: 'SUICIDALITY', label: 'Suicidality' },
  { key: 'GENERALISED_ANXIETY', label: 'Generalised Anxiety' },
  { key: 'DEPRESSION', label: 'Depression' },
  { key: 'BIPOLAR', label: 'Bipolar' },
  { key: 'PTSD', label: 'PTSD' },
  { key: 'DTD', label: 'DTD' },
  { key: 'PSYCHOSIS', label: 'Psychosis' },
  { key: 'SUBSTANCE_ABUSE', label: 'Substance abuse' },
]

export const GENERAL = [
  ['chiefConcern', 'Chief Concern'],
  ['currentMediciationsSupplementsAndVitamins', 'Current Medications, Supplements, and Vitamins'],
  ['pastMedications', 'Past Medications and Treatments'],
  ['sideEffects', 'Side Effects of Medications and Treatments'],
  ['allergies', 'Allergies'],
] as const

export const SERVICE_DELIVERY_OPTIONS: KeyedOption[] = [
  { key: 'CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH', label: 'Service provided via telehealth' },
  {
    key: 'CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE',
    label: 'Service provided via HIPAA compliant software',
  },
  { key: 'CONFIRM_CALL_TIME_AND_LOCATION', label: 'Participant confirmed location at time of call' },
]

export const INTERVENTIONS_OPTIONS: KeyedOption[] = [
  { key: 'COGNITIVE_BEHAVIORAL_THERAPY_TECHNIQUES', label: 'Cognitive Behavioral Therapy (CBT) techniques' },
  { key: 'COGNITIVE_RESTRUCTURING', label: 'Cognitive restructuring' },
  { key: 'COPING_SKILLS_DEVELOPMENT', label: 'Coping skills development' },
  { key: 'RELAPSE_PREVENTION_PLANNING', label: 'Relapse prevention planning' },
  { key: 'MINDFULNESS_TECHNIQUES', label: 'Mindfulness techniques' },
  { key: 'COMMUNICATION_SKILLS_TRAINING', label: 'Communication skills training' },
  { key: 'SOLUTION_FOCUSED_BRIEF_THERAPY_TECHNIQUES', label: 'Solution-Focused Brief Therapy (SFBT) techniques' },
  { key: 'CRISIS_INTERVENTION', label: 'Crisis intervention (if applicable)' },
  { key: 'MOTIVATIONAL_INTERVIEWING_TECHNIQUES', label: 'Motivational interviewing (MI) techniques' },
  { key: 'RESOURCE_LINKAGE', label: 'Resource linkage' },
]

export const PROTECTIVE_FACTORS_OPTIONS: KeyedOption[] = [
  { key: 'STRONG_SOCIAL_SUPPORT', label: 'Strong social support' },
  { key: 'ENGAGEMENT_IN_THERAPY', label: 'Engagement in therapy' },
  { key: 'COPING_SKILLS', label: 'Coping skills' },
  { key: 'FUTURE_ORIENTED_THINKING', label: 'Future-oriented thinking' },
  { key: 'MOTIVATED_FOR_TREATMENT', label: 'Motivated for treatment' },
  { key: 'RELIGIOUS_BELIEFS', label: 'Religious/Spiritual beliefs' },
]

export const NEXT_STEPS_OPTIONS: KeyedOption[] = [
  { key: 'SAFETY_PLAN_REVIEWED_EMAILED', label: 'Safety plan developed, reviewed and emailed to client' },
  { key: 'SAFETY_PLAN_DOCUMENTED_IN_RECORD', label: 'Safety plan documented in medical record' },
  { key: 'NO_FURTHER_ASSISTANCE_NEEDED', label: 'Safety plan not required due to passive SI/low risk' },
  {
    key: 'LAW_ENFORCEMENT_OR_EMERGENCY_CONTACT_NOTIFIED',
    label: 'Law enforcement/emergency contact notified due to imminent risk',
  },
]

export const PARTICIPANT_RESPONSE_OPTIONS: KeyedOption[] = [
  {
    key: 'ENGAGED_AND_SHOWED_UNDERSTANDING_OF_TECHNIQUES',
    label: 'Engaged in discussion and showed understanding of techniques',
  },
  { key: 'ACTIVE_IN_PROBLEM_SOLVING', label: 'Took an active role in problem-solving and generating solutions' },
  { key: 'HIGHLIGHTED_PERSONAL_STRENGTH_AND_RESOURCES', label: 'Highlighted personal strengths and resources' },
  { key: 'PRACTICED_AND_BUILT_CONFIDENCE', label: 'Practiced and built confidence with new skills' },
  {
    key: 'EXPRESSED_MOTIVATION_TO_APPLY_TECHNIQUES',
    label: 'Expressed motivation to apply strategies beyond the session',
  },
]

export const PLAN_OPTIONS: KeyedOption[] = [
  {
    key: 'CONTINUE_SUPPORT_VIA_APP_CHAT',
    label: 'Continue to offer support and personalized treatment via in-app chat',
  },
  { key: 'OFFER_CHECK_IN_CALLS_AS_NEEDED', label: 'Offer check-in calls as clinically appropriate' },
  {
    key: 'DISENROLL_PARTICIPANT_AT_REQUEST',
    label: 'Disenroll participant at their request or due to engagement issues',
  },
  { key: 'SEND_RESOURCES_AND_INFORMATION', label: 'Send resources as discussed during the call' },
]

export const EXPECTED_ACHIEVEMENTS_OPTIONS: KeyedOption[] = [
  { key: 'CONTROL_EMOTIONS_AND_BEHAVIOUR', label: 'Control over emotions and behavior' },
  { key: 'SELF_ESTEEM_AND_CONFIDENCE', label: 'Self-esteem and confidence' },
  { key: 'SENSE_OF_CALM', label: 'Sense of calm' },
  { key: 'MOTIVATION_AND_ENERGY', label: 'Motivation and energy' },
  { key: 'OTHER', label: 'Other' },
]
